import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import { 
  FileText, 
  MoreHorizontal, 
  Copy, 
  CheckCircle, 
  Download, 
  Share2, 
  PlusCircle, 
  ShieldCheck, 
  Link2, 
  Trash2,
  Edit2,
  Archive,
  Clock,
  UserCheck,
  Lock,
  ArrowLeft
} from 'lucide-react';

// Label shown for each stored verification status; anything else is still pending.
const VERIFICATION_LABELS = new Map([
  ['verified', 'Verified'],
  ['failed', 'Tampered'],
]);

export default function FileDetails() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const initialFile = state?.file;
  const initialOptions = state?.options || {};
  const [activeTab, setActiveTab] = useState('Overview');
  const [copiedCID, setCopiedCID] = useState(false);
  const [copiedHash, setCopiedHash] = useState(false);

  const [currentFile, setCurrentFile] = useState(initialFile);
  const [verificationState, setVerificationState] = useState(
    VERIFICATION_LABELS.get(initialFile?.verification_status) ?? 'Pending Verification'
  );
  const [verifiedTimestamp, setVerifiedTimestamp] = useState(
    initialFile?.verified_at ? new Date(String(initialFile.verified_at).replace(' ', 'T')).toLocaleString() : 'Never Verified'
  );
  const [uploadTimestamp, setUploadTimestamp] = useState(
    initialFile?.uploaded_at || "Unknown"
  );

  const [showDropdown, setShowDropdown] = useState(false);
  const versionInputRef = useRef(null);

  if (!currentFile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-6">
        <p className="text-slate-500 mb-4 font-medium" style={{ fontSize: '14px' }}>No file data found. Please upload a file first.</p>
        <button
          onClick={() => navigate('/drive')}
          className="bg-blue-600 text-white rounded-lg transition"
          style={{
            fontSize: '14px',
            height: '44px',
            padding: '0 24px',
            borderRadius: '10px',
            background: '#2563EB',
            border: 'none',
            cursor: 'pointer',
            fontFamily: "'Inter','Segoe UI',sans-serif",
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#1D4ED8'}
          onMouseLeave={e => e.currentTarget.style.background = '#2563EB'}
        >
          Back to My Drive
        </button>
      </div>
    );
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const generatedCid = currentFile.cid || "N/A";
  const generatedHash = currentFile.file_hash || "Not Available";
  const fileSizeReadable = formatBytes(currentFile.file_size || 0);

  const tabs = ['Overview', 'Versions', 'Activity', 'Access', 'Share'];

  const handleDownload = () => {
    window.open(
  `${API_URL}/api/secure-drive/download/${currentFile.id}?owner_email=${encodeURIComponent(localStorage.getItem("email") || "")}`,
  "_blank"
);
  };

  const handleDeletePermanently = async () => {
    if (!window.confirm("Permanently delete this file from Secure Vault? This cannot be undone.")) return;
    try {
      const ownerEmail = localStorage.getItem("email") || "";
      const res = await fetch(
        `${API_URL}/api/secure-drive/delete/${currentFile.id}?owner_email=${encodeURIComponent(ownerEmail)}`,
        { method: "DELETE" }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success !== false) {
        alert("File permanently deleted.");
        navigate("/drive");
      } else {
        alert(data.message || "Could not delete the file.");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting file.");
    }
  };

  const handleGenerateLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Shareable configuration link copied to clipboard!');
  };

  const handleVerifyIntegrity = async () => {
    const response = await fetch(
  `${API_URL}/api/secure-drive/verify/${currentFile.id}`
);
    const data = await response.json();
    if (data.valid) {
      setVerificationState("Verified");
      setVerifiedTimestamp(new Date().toLocaleString());
    } else {
      setVerificationState("Tampered");
    }
  };

  const handleVersionChange = (e) => {
    const selectedVariant = e.target.files[0];
    if (selectedVariant) {
      setCurrentFile(selectedVariant);
      setUploadTimestamp(new Date().toLocaleString() + ' (UTC)');
      setVerificationState('Pending Verification');
      setVerifiedTimestamp('Never Verified');
      alert(`Successfully advanced to a new file tracking variant:\n${selectedVariant.name}`);
    }
    e.target.value = '';
  };

  const handleCopyText = (text, setCopiedState) => {
    navigator.clipboard.writeText(text);
    setCopiedState(true);
    setTimeout(() => setCopiedState(false), 2000);
  };

  const handleMyDriveClick = () => navigate('/drive');

  return (
    <div className="min-h-screen bg-white text-slate-800 antialiased" style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <input type="file" ref={versionInputRef} onChange={handleVersionChange} className="hidden" />

      {/* Header */}
      <div style={{ marginBottom: "32px", padding: "28px 28px 0 28px" }}>
        <h1 style={{ fontSize: "17px", fontWeight: 700, color: "#0F172A", margin: "0 0 8px" }}>
          File Details
        </h1>

        {/* Top Navbar */}
        <div className="flex items-center space-x-2 text-slate-500" style={{ fontSize: '14px' }}>
          <ArrowLeft size={13} className="text-slate-400" />
          <span
            onClick={handleMyDriveClick}
            className="hover:underline cursor-pointer text-blue-600 hover:text-blue-800 font-medium"
          >
            My Drive
          </span>
          <span>&gt;</span>
          <span className="text-slate-700 font-medium max-w-[250px] truncate">{currentFile.file_name}</span>
        </div>
      </div>

      <main className="max-w-6xl mx-auto p-6 space-y-6 bg-white">
        {/* File Header Section */}
        <div className="flex items-center justify-between bg-white p-2 rounded-xl border border-slate-200 shadow-sm relative">
          <div className="flex items-center space-x-4 max-w-full overflow-hidden">
            <div className="p-3 bg-red-50 rounded-lg border border-red-100 text-red-500 flex-shrink-0">
              <FileText size={20} />
            </div>
            <div className="overflow-hidden">
              <h1 className="font-semibold text-slate-900 truncate" style={{ fontSize: '16px' }}>{currentFile.file_name}</h1>
              <p className="text-slate-500" style={{ fontSize: '14px' }}>{fileSizeReadable} • Document Object</p>
            </div>
          </div>

          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition flex-shrink-0 focus:outline-none"
            >
              <MoreHorizontal size={18} />
            </button>

            {showDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl border border-slate-200 shadow-lg py-1.5 z-20">
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      const name = prompt("Rename document:", currentFile.file_name);
                      if (name) { setCurrentFile({ ...currentFile, file_name: name }); }
                    }}
                    className="w-full flex items-center space-x-2.5 px-4 py-2 text-slate-700 hover:bg-slate-50 transition text-left"
                    style={{ fontSize: '14px' }}
                  >
                    <Edit2 size={14} className="text-slate-400" />
                    <span>Rename file</span>
                  </button>
                  <button
                    onClick={() => { setShowDropdown(false); alert('Archive requested.'); }}
                    className="w-full flex items-center space-x-2.5 px-4 py-2 text-slate-700 hover:bg-slate-50 transition text-left"
                    style={{ fontSize: '14px' }}
                  >
                    <Archive size={14} className="text-slate-400" />
                    <span>Archive variant</span>
                  </button>
                  <div className="border-t border-slate-100 my-1" />
                  <button
                    onClick={() => { setShowDropdown(false); handleDeletePermanently(); }}
                    className="w-full flex items-center space-x-2.5 px-4 py-2 text-red-600 hover:bg-red-50 transition text-left"
                    style={{ fontSize: '14px' }}
                  >
                    <Trash2 size={14} className="text-red-400" />
                    <span>Delete permanently</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-slate-200">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  if (tab === 'Versions') {
                    navigate('/file-version', { state: { file: currentFile } });
                  } else if (tab === 'Share') {
                    navigate('/share', { state: { file: currentFile } });
                  } else if (tab === 'Activity') {
                    navigate('/activity', { state: { file: currentFile } });
                  } else {
                    setActiveTab(tab);
                  }
                }}
                className={`pb-3 font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
                style={{ fontSize: '14px' }}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'Overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in duration-200 bg-white">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6 bg-white">

              {/* File Information Card */}
              <section className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h2 className="font-bold text-slate-900 mb-4 tracking-wide uppercase" style={{ fontSize: '11px' }}>File Information</h2>
                <div className="space-y-4" style={{ fontSize: '14px' }}>
                  <div className="grid grid-cols-3 py-1">
                    <span className="text-slate-500">File Name</span>
                    <span className="col-span-2 font-medium text-slate-800 break-all">{currentFile.file_name}</span>
                  </div>
                  <div className="grid grid-cols-3 py-1 items-start">
                    <span className="text-slate-500">CID (IPFS)</span>
                    <div className="col-span-2 flex items-center space-x-2 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                      <span className="font-mono text-slate-600 break-all" style={{ fontSize: '11px' }}>{generatedCid}</span>
                      <button onClick={() => handleCopyText(generatedCid, setCopiedCID)} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
                        {copiedCID ? <CheckCircle size={13} className="text-green-500" /> : <Copy size={13} />}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 py-1">
                    <span className="text-slate-500">Size</span>
                    <span className="col-span-2 text-slate-800 font-medium">{fileSizeReadable}</span>
                  </div>
                  <div className="grid grid-cols-3 py-1">
                    <span className="text-slate-500">Owner</span>
                    <span className="col-span-2 text-slate-800 font-medium">{currentFile.owner_email || "Unknown"}</span>
                  </div>
                  <div className="grid grid-cols-3 py-1">
                    <span className="text-slate-500">Uploaded On</span>
                    <span className="col-span-2 text-slate-800 font-medium">{uploadTimestamp}</span>
                  </div>
                  <div className="grid grid-cols-3 py-1">
                    <span className="text-slate-500">File Type</span>
                    <span className="col-span-2 text-slate-600 font-mono bg-slate-100 px-2 py-0.5 rounded w-max break-all" style={{ fontSize: '11px' }}>
                      {currentFile.file_type || "Unknown"}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 py-1">
                    <span className="text-slate-500">Encryption</span>
                    <span className="col-span-2 text-slate-800 font-medium">
                      {initialOptions.encrypt ? 'AES-256 (Client-Side)' : 'None (Plaintext Upload)'}
                    </span>
                  </div>
                </div>
              </section>

              {/* Integrity Card */}
              <section className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h2 className="font-bold text-slate-900 mb-4 tracking-wide uppercase" style={{ fontSize: '11px' }}>Integrity (CID Verification)</h2>
                <div className="space-y-4" style={{ fontSize: '14px' }}>
                  <div className="grid grid-cols-3 py-1 items-center">
                    <span className="text-slate-500">Status</span>
                    <span className={`col-span-2 font-semibold flex items-center space-x-1 ${
                      verificationState === 'Verified' ? 'text-emerald-600' : 'text-amber-500'
                    }`}>
                      <span className={`w-2 h-2 rounded-full inline-block mr-1.5 ${
                        verificationState === 'Verified' ? 'bg-emerald-500' : 'bg-amber-400 animate-pulse'
                      }`}></span>
                      {verificationState}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 py-1">
                    <span className="text-slate-500">Last Verified</span>
                    <span className="col-span-2 text-slate-800 font-medium">{verifiedTimestamp}</span>
                  </div>
                  <div className="grid grid-cols-3 py-1 items-start">
                    <span className="text-slate-500">Integrity Hash (SHA-256)</span>
                    <div className="col-span-2 flex items-center space-x-2 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                      <span className="font-mono text-slate-600 break-all" style={{ fontSize: '11px' }}>{generatedHash}</span>
                      <button onClick={() => handleCopyText(generatedHash, setCopiedHash)} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
                        {copiedHash ? <CheckCircle size={13} className="text-green-500" /> : <Copy size={13} />}
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Right Column */}
            <div className="space-y-6 bg-white">
              {/* IPFS Card */}
              <section className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h2 className="font-bold text-slate-900 mb-4 tracking-wide uppercase" style={{ fontSize: '11px' }}>IPFS Information</h2>
                <div className="space-y-3 mb-5" style={{ fontSize: '14px' }}>
                  <div className="flex justify-between py-0.5">
                    <span className="text-slate-500">Network</span>
                    <span className="font-medium text-slate-800">IPFS Network</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-slate-500">Pinned</span>
                    <span className="font-semibold text-emerald-600">{initialOptions.pin ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-slate-500">Replication</span>
                    <span className="font-medium text-slate-800">{initialOptions.redundancy || 'High'}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-slate-500">Storage Provider</span>
                    <span className="font-medium text-slate-800">IPFS Cluster Node</span>
                  </div>
                </div>

                {generatedCid !== "N/A" && (
                  <a
                    href={`https://ipfs.io/ipfs/${generatedCid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on IPFS Gateway
                  </a>
                )}
              </section>

              {/* Quick Actions Card */}
              <section className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h2 className="font-bold text-slate-900 mb-4 tracking-wide uppercase" style={{ fontSize: '11px' }}>Quick Actions</h2>
                <div className="flex flex-col space-y-1">
                  <button onClick={handleDownload} className="flex items-center space-x-3 w-full p-2.5 rounded-lg text-slate-700 hover:bg-slate-50 transition font-medium group" style={{ fontSize: '14px' }}>
                    <Download size={16} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                    <span>Download</span>
                  </button>
                  <button onClick={() => navigate('/share', { state: { file: currentFile } })} className="flex items-center space-x-3 w-full p-2.5 rounded-lg text-slate-700 hover:bg-slate-50 transition font-medium group" style={{ fontSize: '14px' }}>
                    <Share2 size={16} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                    <span>Share</span>
                  </button>
                  <button onClick={() => versionInputRef.current.click()} className="flex items-center space-x-3 w-full p-2.5 rounded-lg text-slate-700 hover:bg-slate-50 transition font-medium group" style={{ fontSize: '14px' }}>
                    <PlusCircle size={16} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                    <span>Create New Version</span>
                  </button>
                  <button onClick={handleVerifyIntegrity} className="flex items-center space-x-3 w-full p-2.5 rounded-lg text-slate-700 hover:bg-slate-50 transition font-medium group" style={{ fontSize: '14px' }}>
                    <ShieldCheck size={16} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                    <span>Verify Integrity</span>
                  </button>
                  <button onClick={handleGenerateLink} className="flex items-center space-x-3 w-full p-2.5 rounded-lg text-slate-700 hover:bg-slate-50 transition font-medium group" style={{ fontSize: '14px' }}>
                    <Link2 size={16} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                    <span>Generate Link</span>
                  </button>
                </div>
              </section>
            </div>
          </div>
        )}

        {/* ACTIVITY TAB */}
        {activeTab === 'Activity' && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4 animate-in fade-in duration-200">
            <h2 className="font-bold text-slate-900 uppercase tracking-wide pb-2 border-b border-slate-100" style={{ fontSize: '11px' }}>Immutable Ledger Node Audit Log</h2>
            <div className="divide-y divide-slate-100" style={{ fontSize: '14px' }}>
              <div className="py-3 flex justify-between items-start">
                <div className="flex space-x-3">
                  <div className="p-1.5 bg-blue-50 text-blue-600 rounded-md mt-0.5"><Clock size={15} /></div>
                  <div>
                    <p className="font-medium text-slate-800">File loaded into working context memory</p>
                    <p className="text-slate-400" style={{ fontSize: '11px' }}>Target payload structural checks passed completely</p>
                  </div>
                </div>
                <span className="font-mono text-slate-400" style={{ fontSize: '11px' }}>{uploadTimestamp}</span>
              </div>
              <div className="py-3 flex justify-between items-start">
                <div className="flex space-x-3">
                  <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-md mt-0.5"><ShieldCheck size={15} /></div>
                  <div>
                    <p className="font-medium text-slate-800">Dynamic SHA-256 integrity hash tree computed</p>
                    <p className="text-slate-400 font-mono bg-slate-50 p-1 rounded mt-1 max-w-lg truncate" style={{ fontSize: '11px' }}>{generatedHash}</p>
                  </div>
                </div>
                <span className="font-mono text-slate-400" style={{ fontSize: '11px' }}>{uploadTimestamp}</span>
              </div>
            </div>
          </div>
        )}

        {/* ACCESS TAB */}
        {activeTab === 'Access' && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4 animate-in fade-in duration-200">
            <h2 className="font-bold text-slate-900 uppercase tracking-wide pb-2 border-b border-slate-100" style={{ fontSize: '11px' }}>Access Permissions Configuration</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 text-blue-700 rounded-lg"><UserCheck size={16} /></div>
                  <div>
                    <p className="font-semibold text-slate-800" style={{ fontSize: '14px' }}>Owner Node (You)</p>
                    <p className="text-slate-500" style={{ fontSize: '11px' }}>Full administrative, rewrite, read control signatures</p>
                  </div>
                </div>
                <span className="font-mono bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-100 font-medium" style={{ fontSize: '11px' }}>Full Access</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white border border-slate-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-slate-100 text-slate-600 rounded-lg"><Lock size={16} /></div>
                  <div>
                    <p className="font-medium text-slate-800" style={{ fontSize: '14px' }}>Public Network Peers</p>
                    <p className="text-slate-500" style={{ fontSize: '11px' }}>External node permissions via strict Gateway link distribution</p>
                  </div>
                </div>
                <span className="font-mono bg-amber-50 text-amber-700 px-2 py-1 rounded border border-amber-100 font-medium" style={{ fontSize: '11px' }}>Gateway Only</span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}